import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  User, Mail, Phone, Building2, Save
} from 'lucide-react';
import { toast } from 'sonner';

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

export default function ProfilePage() {
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', company: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const profileRes = await API.get('/user/profile');
        setProfile({
          name: profileRes.data.name || '',
          email: profileRes.data.email || '',
          phone: profileRes.data.phone || '',
          company: profileRes.data.company || '',
        });
      } catch {} finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put('/user/profile', {
        name: profile.name,
        phone: profile.phone,
        company: profile.company,
      });
      setProfile(prev => ({ ...prev, ...res.data }));
      toast.success('Profile updated');
    } catch { toast.error('Failed to save profile'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="max-w-2xl space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-200 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="profile-page" className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-500 mt-1">This information appears on your deal blast emails.</p>
      </div>

      {/* Profile Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-4 pb-2">
            <div className="h-14 w-14 rounded-full bg-[#0073ea]/10 flex items-center justify-center">
              <User className="h-7 w-7 text-[#0073ea]" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">{profile.name || 'Your Name'}</p>
              <p className="text-sm text-gray-500">{profile.email}</p>
            </div>
          </div>

          <Separator className="bg-gray-200" />

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Full Name
              </Label>
              <Input data-testid="profile-name" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> Email
              </Label>
              <Input data-testid="profile-email" value={profile.email} disabled className="bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed" />
              <p className="text-[10px] text-gray-400">Email is linked to your Google account and cannot be changed.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> Phone Number
              </Label>
              <Input data-testid="profile-phone" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="(305) 555-1234" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> Company Name
              </Label>
              <Input data-testid="profile-company" value={profile.company} onChange={e => setProfile(p => ({ ...p, company: e.target.value }))} placeholder="Your company or business name" className={inputClass} />
            </div>
          </div>

          <Separator className="bg-gray-200" />

          {/* Email Signature Preview */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Email Signature Preview</p>
            <div className="rounded-lg border border-gray-200 bg-[#1a1a2e] p-4">
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Contact</p>
                {profile.name && <p className="text-sm font-semibold text-white">{profile.name}</p>}
                {profile.company && <p className="text-xs text-gray-400">{profile.company}</p>}
                {profile.email && <p className="text-xs text-[#ff6b35]">{profile.email}</p>}
                {profile.phone && <p className="text-xs text-gray-400">{profile.phone}</p>}
              </div>
            </div>
          </div>

          <Button data-testid="save-profile-btn" onClick={handleSave} disabled={saving} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
            {saving ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save Profile</>}
          </Button>
        </CardContent>
      </Card>

    </div>
  );
}
